import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { registerScrollSmoother, unregisterScrollSmoother, cleanupAllGSAP } from './gsapCleanup';

// Store reference to current ScrollSmoother instance
let currentScrollSmoother = null;
let initializationPromise = null;

/**
 * Check if ScrollTrigger is properly initialized and ready
 */
export const isScrollTriggerReady = () => {
  try {
    return (
      ScrollTrigger &&
      typeof ScrollTrigger.refresh === 'function' &&
      ScrollTrigger.isTouch !== undefined
    );
  } catch (error) {
    return false;
  }
};

/**
 * Wait for ScrollSmoother to be fully ready
 */
const waitForScrollSmootherReady = () => {
  return new Promise((resolve) => {
    const checkReady = () => {
      if (currentScrollSmoother && currentScrollSmoother.scrollTrigger) {
        resolve(currentScrollSmoother);
      } else {
        requestAnimationFrame(checkReady);
      }
    };
    checkReady();
  });
};

/**
 * Utility function to safely initialize ScrollSmoother
 * This prevents multiple instances and ensures proper cleanup
 */
export const initializeScrollSmoother = (config = {}) => {
  // Return existing promise if already initializing
  if (initializationPromise) {
    return initializationPromise;
  }

  // Clean up any existing ScrollSmoother instance
  if (currentScrollSmoother) {
    unregisterScrollSmoother();
    currentScrollSmoother.kill();
    currentScrollSmoother = null;
  }

  // Default configuration
  const defaultConfig = {
    wrapper: '#smooth-wrapper',
    content: '#smooth-content',
    smooth: 1.2,
    effects: false,
    normalizeScroll: true,
    ignoreMobileResize: true, // Keep true for performance, handle orientation separately
    smoothTouch: 0.1,
    ease: 'power2.out',
    autoResize: true
  };

  // Merge with provided config
  const finalConfig = { ...defaultConfig, ...config };

  initializationPromise = new Promise((resolve) => {
    const initializeAsync = async () => {
      try {
        // Create new ScrollSmoother instance
        currentScrollSmoother = ScrollSmoother.create(finalConfig);

        // Register for global cleanup
        registerScrollSmoother(currentScrollSmoother);

        // Wait for ScrollSmoother to be fully ready
        await waitForScrollSmootherReady();

        // Ensure ScrollTrigger is ready
        if (isScrollTriggerReady()) {
          ScrollTrigger.refresh();
        }

        resolve(currentScrollSmoother);
      } catch (error) {
        console.warn('ScrollSmoother initialization failed:', error);
        resolve(null);
      } finally {
        initializationPromise = null;
      }
    };

    initializeAsync();
  });

  return initializationPromise;
};

/**
 * Clean up ScrollSmoother instance
 */
export const cleanupScrollSmoother = () => {
  // Use comprehensive cleanup system
  cleanupAllGSAP();

  // Reset local references
  currentScrollSmoother = null;
  initializationPromise = null;
};
